/* Spectrum graph section (sections/freddie-spectrum.liquid).
 * Draws the light-source spectra from spectrum-graph-data.js into an inline SVG
 * and animates between sources and the with/without-Freddie states.
 * All colors come from CSS (scheme tokens) — JS only writes geometry and
 * data-on / aria state.
 */
(function () {
  'use strict';
  if (customElements.get('spectrum-graph')) return;

  var SVG_NS = 'http://www.w3.org/2000/svg';
  var BAND = { start: 380, end: 570 };
  var X_MIN = 380;
  var X_MAX = 700;

  /* The viewBox width always equals the rendered width (1:1 scale, so type
   * renders at true pixel size at any container width). Two chrome tiers:
   * wide plots get the full furniture, narrow ones keep just curve, zone,
   * rainbow rail, and 4 ticks. */
  var CHROME = {
    full: {
      ml: 52,
      mr: 26,
      mt: 26,
      plotH: 220,
      fs: 11,
      fsLabel: 10.5,
      xTicks: [400, 450, 500, 550, 600, 650, 700],
      yTicks: [0, 50, 100],
      bandLabelText: true,
      legend: true,
      bottomPad: 52
    },
    compact: {
      ml: 10,
      mr: 22,
      mt: 24,
      plotH: 185,
      fs: 10,
      fsLabel: 9,
      xTicks: [400, 500, 600, 700],
      yTicks: [],
      bandLabelText: false,
      legend: false,
      bottomPad: 46
    }
  };
  var FULL_MIN_WIDTH = 460;
  var Y_GRID = [0, 25, 50, 75, 100];

  function el(name, attrs, cls) {
    var node = document.createElementNS(SVG_NS, name);
    for (var k in attrs) node.setAttribute(k, attrs[k]);
    if (cls) node.setAttribute('class', cls);
    return node;
  }

  class SpectrumGraph extends HTMLElement {
    connectedCallback() {
      var data = window.FreddieSpectrumData;
      var configEl = this.querySelector('[data-sg-config]');
      if (!data || !configEl) return;
      this.data = data;
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (err) {
        return;
      }

      this.svg = this.querySelector('[data-sg-svg]');
      this.statValue = this.querySelector('[data-sg-stat]');
      this.badge = this.querySelector('[data-sg-badge]');
      this.toggle = this.querySelector('[data-sg-toggle]');
      this.live = this.querySelector('[data-sg-live]');
      this.chips = Array.prototype.slice.call(this.querySelectorAll('[data-sg-source]'));
      if (!this.svg || !this.toggle) return;

      /* per-block "brightness at the eye" overrides from the theme editor */
      this.luxOverride = {};
      var overrides = this.luxOverride;
      this.chips.forEach(function (chip) {
        var lux = parseFloat(chip.dataset.sgLux);
        if (lux > 0) overrides[chip.dataset.sgSource] = lux;
      });

      var firstChip = this.chips[0];
      this.state = {
        key: this.config.initial || (firstChip && firstChip.dataset.sgSource),
        on: false
      };
      if (!this.data.sources[this.state.key]) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.chartW = 0;
      this.raf = null;

      this.buildDisplay();
      this.buildChart();
      this.cur = this.targetSamples();
      this.statCur = this.targetStat();
      this.paint();
      this.updateControls();
      this.announce();
      this.bind();

      var self = this;
      this.resizeObserver = new ResizeObserver(function () {
        var w = Math.round(self.svg.getBoundingClientRect().width);
        if (w && Math.abs(w - self.chartW) > 4) {
          self.buildChart();
          self.paint();
        }
      });
      this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
      if (this.raf) cancelAnimationFrame(this.raf);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    bind() {
      var self = this;
      this.toggle.addEventListener('click', function () {
        self.state.on = !self.state.on;
        self.updateControls();
        self.tween();
        self.announce();
      });
      this.chips.forEach(function (chip) {
        chip.addEventListener('click', function () {
          var key = chip.dataset.sgSource;
          if (key === self.state.key || !self.data.sources[key]) return;
          self.state.key = key;
          self.updateControls();
          self.tween();
          self.announce();
        });
      });
    }

    /* Display curves: the shipped vals (already 5nm-smoothed for display) are
     * area-normalized over 400-700nm across this element's sources, then one
     * shared scale fits the tallest point to 100 - so switching sources
     * compares equal total energy, and no single narrow peak sets the scale
     * for everyone else. Stats never touch these; they come from raw-data
     * scalars (mder/tmel). */
    buildDisplay() {
      var data = this.data;
      var wls = this.wavelengths();
      var keys = this.chips.map(function (chip) { return chip.dataset.sgSource; });
      if (keys.indexOf(this.state.key) === -1) keys.push(this.state.key);
      var normed = {};
      var peak = 0;
      keys.forEach(function (k) {
        var src = data.sources[k];
        if (!src || normed[k]) return;
        var area = 0;
        src.vals.forEach(function (v, i) {
          if (wls[i] >= 400 && wls[i] <= 700) area += v;
        });
        if (!area) return;
        var scaled = src.vals.map(function (v) { return v / area; });
        normed[k] = scaled;
        scaled.forEach(function (v) { if (v > peak) peak = v; });
      });
      this.display = {};
      for (var k in normed) {
        this.display[k] = normed[k].map(function (v) { return v * 100 / peak; });
      }
    }

    displayVals(key) {
      return this.display[key] || this.data.sources[key].vals;
    }

    /* --- geometry ------------------------------------------------------- */

    wavelengths() {
      if (!this._wls) {
        this._wls = [];
        for (var i = 0; i < this.data.n; i++) this._wls.push(this.data.wl0 + i * this.data.step);
      }
      return this._wls;
    }

    xOf(wl) {
      var L = this.L;
      return L.ml + ((wl - X_MIN) / (X_MAX - X_MIN)) * this.plotW;
    }

    yOf(v) {
      var L = this.L;
      return L.mt + (1 - v / 100) * L.plotH;
    }

    pathOf(samples) {
      var wls = this.wavelengths();
      var d = '';
      for (var i = 0; i < samples.length; i++) {
        d += (i ? 'L' : 'M') + this.xOf(wls[i]).toFixed(1) + ',' + this.yOf(samples[i]).toFixed(1) + ' ';
      }
      return d.trim();
    }

    targetSamples() {
      var vals = this.displayVals(this.state.key);
      var lens = this.data.lens;
      var on = this.state.on;
      return vals.map(function (v, i) {
        return on ? v * lens[i] / 100 : v;
      });
    }

    /* Multiple of the 10 melanopic lux evening limit (Brown et al. 2022):
     * (photopic lux at the eye x melanopic DER) / 10, scaled by the lens's
     * melanopic transmission when Freddie is on. */
    targetStat() {
      var src = this.data.sources[this.state.key];
      var lux = this.luxOverride[this.state.key] || src.lux;
      var mult = lux * src.mder / 10;
      return this.state.on ? mult * src.tmel / 100 : mult;
    }

    /* --- static chrome, rebuilt on layout change ------------------------ */

    buildChart() {
      var w = Math.round(this.svg.getBoundingClientRect().width) || 340;
      this.chartW = w;
      var L = this.L = Object.assign({ vbWidth: w }, CHROME[w >= FULL_MIN_WIDTH ? 'full' : 'compact']);
      this.plotW = L.vbWidth - L.ml - L.mr;
      this.legend = null;
      var yBottom = L.mt + L.plotH;
      var vbHeight = yBottom + L.bottomPad;
      var svg = this.svg;
      svg.setAttribute('viewBox', '0 0 ' + L.vbWidth + ' ' + vbHeight);
      svg.textContent = '';

      var defs = el('defs', {});
      var grad = el('linearGradient', { id: 'sg-specband-' + (this.config.uid || '0'), x1: 0, y1: 0, x2: 1, y2: 0 });
      [['0', '#6a48c8'], ['0.22', '#4a6cf0'], ['0.38', '#2f9e74'], ['0.53', '#8ab84a'],
       ['0.66', '#e0b93f'], ['0.78', '#dd6a38'], ['1', '#b33a2e']].forEach(function (stop) {
        grad.appendChild(el('stop', { offset: stop[0], 'stop-color': stop[1] }));
      });
      defs.appendChild(grad);
      svg.appendChild(defs);

      var self = this;
      Y_GRID.forEach(function (v) {
        var y = self.yOf(v);
        svg.appendChild(el('line', { x1: L.ml, y1: y, x2: L.ml + self.plotW, y2: y },
          v === 0 ? 'spectrum-graph__axis' : 'spectrum-graph__grid'));
      });
      L.yTicks.forEach(function (v) {
        var tick = el('text', { x: L.ml - 10, y: self.yOf(v) + 4, 'text-anchor': 'end', 'font-size': L.fs }, 'spectrum-graph__tick');
        tick.textContent = v;
        svg.appendChild(tick);
      });

      if (this.config.showBand) {
        var bx = this.xOf(BAND.start);
        var bw = this.xOf(BAND.end) - bx;
        var band = el('g', {}, 'spectrum-graph__band');
        band.appendChild(el('rect', { x: bx, y: L.mt, width: bw, height: L.plotH }, 'spectrum-graph__band-fill'));
        band.appendChild(el('line', { x1: bx, y1: L.mt, x2: bx, y2: yBottom }, 'spectrum-graph__band-edge'));
        band.appendChild(el('line', { x1: bx + bw, y1: L.mt, x2: bx + bw, y2: yBottom }, 'spectrum-graph__band-edge'));
        /* label centered over the zone on both tiers; compact drops the nm
           range (truncated at the "·" separator) so it fits narrow viewBoxes */
        var bandText = this.config.bandLabel || '';
        if (!L.bandLabelText) bandText = bandText.split('·')[0].trim();
        var bandLabel = el('text', {
          x: bx + bw / 2, y: L.mt - 10, 'text-anchor': 'middle',
          'font-size': L.fsLabel, 'letter-spacing': L.bandLabelText ? 1.5 : 1.2
        }, 'spectrum-graph__label');
        bandLabel.textContent = bandText.toUpperCase();
        band.appendChild(bandLabel);
        svg.appendChild(band);
      }

      this.basePath = el('path', { fill: 'none' }, 'spectrum-graph__base');
      this.areaPath = el('path', { stroke: 'none' }, 'spectrum-graph__area');
      this.linePath = el('path', { fill: 'none' }, 'spectrum-graph__line');
      svg.appendChild(this.basePath);
      svg.appendChild(this.areaPath);
      svg.appendChild(this.linePath);

      svg.appendChild(el('rect', {
        x: L.ml, y: yBottom + 12, width: this.plotW, height: 8, rx: 4,
        fill: 'url(#' + grad.id + ')'
      }));
      L.xTicks.forEach(function (wl) {
        var tick = el('text', { x: self.xOf(wl), y: yBottom + 42, 'text-anchor': 'middle', 'font-size': L.fs }, 'spectrum-graph__tick');
        tick.textContent = wl + 'nm';
        svg.appendChild(tick);
      });

      if (this.config.showComparison && L.legend) {
        this.legend = el('g', {}, 'spectrum-graph__legend');
        this.legend.appendChild(el('line', {
          x1: L.ml + this.plotW - 144, y1: L.mt - 14, x2: L.ml + this.plotW - 122, y2: L.mt - 14
        }, 'spectrum-graph__base'));
        var legendText = el('text', {
          x: L.ml + this.plotW - 114, y: L.mt - 10, 'text-anchor': 'start', 'font-size': L.fsLabel, 'letter-spacing': 1.5
        }, 'spectrum-graph__label');
        legendText.textContent = (this.config.comparisonLabel || 'WITHOUT FREDDIE').toUpperCase();
        this.legend.appendChild(legendText);
        svg.appendChild(this.legend);
      }
    }

    /* --- dynamic paint --------------------------------------------------- */

    paint() {
      var L = this.L;
      var lineD = this.pathOf(this.cur);
      var yBottom = L.mt + L.plotH;
      this.linePath.setAttribute('d', lineD);
      this.areaPath.setAttribute('d',
        lineD + ' L' + (L.ml + this.plotW) + ',' + yBottom + ' L' + L.ml + ',' + yBottom + ' Z');
      var showBase = this.state.on && this.config.showComparison;
      this.basePath.setAttribute('d', showBase ? this.pathOf(this.displayVals(this.state.key)) : '');
      if (this.legend) this.legend.style.opacity = showBase ? 1 : 0;
      if (this.statValue) this.statValue.textContent = this.formatStat(this.statCur);
      if (this.badge) {
        var over = this.statCur > 1;
        this.badge.classList.toggle('is-over', over);
        this.badge.classList.toggle('is-under', !over);
      }
    }

    formatStat(v) {
      if (v < 0.1) return '<0.1×';
      return (v >= 3 ? Math.round(v) : v.toFixed(1)) + '×';
    }

    updateControls() {
      var on = this.state.on;
      if (on) this.setAttribute('data-on', '');
      else this.removeAttribute('data-on');
      this.toggle.setAttribute('aria-checked', on ? 'true' : 'false');
      var key = this.state.key;
      this.chips.forEach(function (chip) {
        chip.setAttribute('aria-pressed', chip.dataset.sgSource === key ? 'true' : 'false');
      });
    }

    announce() {
      if (!this.live) return;
      var src = this.data.sources[this.state.key];
      var mult = this.targetStat();
      this.live.textContent = src.full + ': ' + this.formatStat(mult) + ' the ' +
        (this.config.statLabel || 'bedtime light limit') +
        (this.state.on ? ' with Freddie on. ' : ' without Freddie. ') +
        (mult > 1 ? 'Over the limit.' : 'Under the limit.');
    }

    tween() {
      var target = this.targetSamples();
      var statTarget = this.targetStat();
      if (this.reducedMotion.matches) {
        this.cur = target;
        this.statCur = statTarget;
        this.paint();
        return;
      }
      if (this.raf) cancelAnimationFrame(this.raf);
      var from = this.cur.slice();
      var statFrom = this.statCur;
      var start = performance.now();
      var duration = 340;
      var self = this;
      var step = function (now) {
        var t = Math.min(1, (now - start) / duration);
        var e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        self.cur = from.map(function (v, i) { return v + (target[i] - v) * e; });
        self.statCur = statFrom + (statTarget - statFrom) * e;
        self.paint();
        if (t < 1) self.raf = requestAnimationFrame(step);
      };
      this.raf = requestAnimationFrame(step);
    }
  }

  customElements.define('spectrum-graph', SpectrumGraph);
})();
